import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api";
import "./videoSection.css";

const PAGE_SIZE = 4;

async function fetchJson(url) {
  const sep = url.includes("?") ? "&" : "?";
  const urlNoCache = `${url}${sep}_=${Date.now()}`;

  const { data } = await api.get(`/video-lectures${urlNoCache}`);

  const isOk = data?.ok === true || data?.success === true;
  if (!isOk) throw new Error(data?.message || "API request failed");

  return data;
}

function getThumbCandidates(videoId) {
  if (!videoId) return [];
  return [
    `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
    `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
    `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
    `https://i.ytimg.com/vi/${videoId}/default.jpg`,
  ];
}

// "1:42:10" / "42:10" from the stream's start/end timestamps.
function formatDuration(start, end) {
  if (!start || !end) return "";
  const total = Math.floor((new Date(end) - new Date(start)) / 1000);
  if (!Number.isFinite(total) || total <= 0) return "";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

// normalize backend LiveStreams shape -> UI shape
function normalize(items) {
  return (items || []).map((x, idx) => {
    const started =
      x?.startedAt || x?.createdAt || x?.updatedAt || x?.lastSeenLiveAt;

    return {
      id: idx + 1,

      titleEn: x?.titleEn || x?.title || "",
      titleUr: x?.titleUr || "",
      title: x?.titleEn || x?.title || "", // alias (thumbnail alt text)
      date: started
        ? new Date(started).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",

      url: x?.url || "",

      livestreamId: x?.livestreamId,
      videoId: x?.videoId,
      status: x?.status,
      duration: formatDuration(x?.startedAt, x?.endedAt),
    };
  });
}

export default function VideoLectures() {
  const navigate = useNavigate();

  const [videoItems, setVideoItems] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const data = await fetchJson(`/history?limit=${PAGE_SIZE}`);
        const items = normalize(data.items || []);

        if (!alive) return;

        setVideoItems(items);
        setCurrentVideo((prev) => prev ?? items[0] ?? null);
      } catch (err) {
        console.error("History fetch failed:", err);
        if (!alive) return;
        setVideoItems([]);
        setCurrentVideo(null);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const [thumbIdx, setThumbIdx] = useState(0);

  const thumbCandidates = useMemo(
    () => getThumbCandidates(currentVideo?.videoId),
    [currentVideo?.videoId],
  );

  useEffect(() => {
    // reset when video changes
    setThumbIdx(0);
  }, [currentVideo?.videoId]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // mobile shows only the first 2 rows (same behavior as before)
  const videosToShow = useMemo(() => {
    return isMobile ? videoItems.slice(0, 2) : videoItems;
  }, [isMobile, videoItems]);

  const goToWatch = (vid) => {
    if (!vid?.livestreamId) return;
    navigate(`/video-lectures/watch/${vid.livestreamId}`);
  };

  return (
    <div className="paddingAdjust bg-[#FAFAFA] text-black">
      <section className="home-video">
        <div className="hv-head">
          <h2 className="hv-title">Latest Videos</h2>
          <a className="hv-viewall" href="/video">
            View All
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        </div>

        <div className="hv-media">
          {/* ===== Featured video (left) ===== */}
          <article
            className="hv-feat"
            onClick={() => goToWatch(currentVideo)}
          >
            <div className="hv-plate">
              {thumbCandidates.length > 0 && (
                <img
                  className="hv-thumb"
                  src={thumbCandidates[thumbIdx] || ""}
                  alt={currentVideo?.title || "Video thumbnail"}
                  onError={() => {
                    setThumbIdx((i) =>
                      Math.min(i + 1, thumbCandidates.length - 1),
                    );
                  }}
                  onLoad={(e) => {
                    // If maxres is missing, YouTube often returns a tiny
                    // placeholder — fall through to the next size.
                    if (
                      e.currentTarget.naturalWidth <= 160 &&
                      thumbIdx < thumbCandidates.length - 1
                    ) {
                      setThumbIdx((i) =>
                        Math.min(i + 1, thumbCandidates.length - 1),
                      );
                    }
                  }}
                />
              )}
              {currentVideo?.status === "live" && (
                <span className="hv-badge-live">
                  <i /> LIVE
                </span>
              )}
              <button
                type="button"
                className="hv-play"
                aria-label="Play video"
                onClick={(e) => {
                  e.stopPropagation();
                  goToWatch(currentVideo);
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
              {currentVideo?.duration && (
                <span className="hv-dur">{currentVideo.duration}</span>
              )}
            </div>
            <div className="hv-body">
              {currentVideo?.titleUr ? (
                <>
                  <h3 className="hv-feat-title-ur" lang="ur" dir="rtl">
                    {currentVideo.titleUr}
                  </h3>
                  {currentVideo?.titleEn && (
                    <p className="hv-feat-title-en">{currentVideo.titleEn}</p>
                  )}
                </>
              ) : (
                <h3 className="hv-feat-title">{currentVideo?.titleEn || ""}</h3>
              )}
              <p className="hv-feat-meta">{currentVideo?.date || ""}</p>
            </div>
          </article>
          {/* ===== End featured video ===== */}

          {/* ===== List (right) ===== */}
          <ul className="hv-list">
            {videosToShow.map((vid) => (
              <li
                key={vid.livestreamId || vid.id}
                className="hv-row"
                onClick={() => goToWatch(vid)}
              >
                <span className="hv-num">
                  {vid.id.toString().padStart(2, "0")}
                </span>
                <div className="hv-row-main">
                  {vid.titleUr ? (
                    <>
                      <p className="hv-row-title-ur" lang="ur" dir="rtl">
                        {vid.titleUr}
                      </p>
                      {vid.titleEn && (
                        <p className="hv-row-title-en">{vid.titleEn}</p>
                      )}
                    </>
                  ) : (
                    <p className="hv-row-title">{vid.titleEn}</p>
                  )}
                  <p className="hv-row-sub">{vid.date}</p>
                </div>
              </li>
            ))}
          </ul>
          {/* ===== End list ===== */}
        </div>
      </section>
    </div>
  );
}
